import { GetAuthMeData } from '@/core/services/types/getAuthMe/responseTypes'
import { GetCountriesResponse } from '@/core/services/types/getCountries/responseTypes'
import { GetLoyaltyPointsDataType } from '@/core/services/types/getLoyaltyPoints/responseTypes'
import { Member } from '@/core/services/types/getMembers/responseTypes'
import { OrderBundlesDataType } from '@/core/services/types/getOrderBundles/responseTypes'
import { OTATokenCookie, OTATokenExpiryCookie } from '@/enums/cookies'
import { getCookie, removeCookie } from '@/utils/cookies'
import { extractOrderBundles } from '@/utils/ota/data'
import * as Sentry from '@sentry/nextjs'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { GraphQLClient, gql } from 'graphql-request'

export * as OTAService from './ota'

const endpoint = `${process.env.NEXT_PUBLIC_MAIN_WEBSITE_BASEURL}/api/graphql`

const graphQLClient = new GraphQLClient(endpoint, {
  headers: {
    authorization: `Bearer ${getCookie(OTATokenCookie)}`
  },
  cache: 'no-cache'
})

export const getAuthMe = async () => {
  try {
    const query = gql`
      {
        meAuthUser {
          user {
            _id
            firstName
            email
            nextPaymentDate
            nextDeliveryDate
            lastPaymentDate
            lastDeliveryDate
            paymentContract {
              status
              active
            }
            orderBundles(order: [{ createdAt: "DESC" }]) {
              edges {
                node {
                  token
                  payment {
                    status
                  }
                  orders {
                    edges {
                      node {
                        returnWindowOpensAt
                        delayedDeliveryDate
                        product {
                          design {
                            name
                          }
                        }
                        shipment {
                          deliveryDate
                          status
                        }
                      }
                    }
                  }
                }
              }
            }
            country {
              _id
              name
              canCancelSubscriptionAfterDays
              expectedDeliveryDateDailyOrderAfterDays
              canDoReturnRequestAfterDay
            }
          }
        }
      }
    `
    const data = await graphQLClient.request<GetAuthMeData>(query)

    const { user } = data.meAuthUser

    return user
  } catch (error) {
    if (error instanceof Error && (error as AxiosError).response?.status === 401) {
      removeCookie(OTATokenCookie)
      removeCookie(OTATokenExpiryCookie)
    }

    if (error instanceof Error) {
      Sentry.captureException({ error })
      throw new Error(error.message)
    }
  }
}

export type getAuthMeUser = Awaited<ReturnType<typeof getAuthMe>>

export const getMembers = async () => {
  try {
    const response: AxiosResponse<Member[]> = await axios.get(`${process.env.NEXT_PUBLIC_MAIN_WEBSITE_BASEURL}/api/v2/members`, {
      headers: {
        authorization: `Bearer ${getCookie(OTATokenCookie)}`
      }
    })

    return response.data
  } catch (error) {
    if (error instanceof Error) {
      Sentry.captureException({ error })
      throw new Error(error.message)
    }
  }
}

export type GetOrderBundlesProps = {
  limit?: number
  after?: string | null
}

export const getOrderBundles = async ({ limit = 10, after = null }: GetOrderBundlesProps = {}) => {
  try {
    const variables = {
      limit,
      after
    }

    const query = gql`
      query OrderBundles($limit: Int = 10, $after: String = null) {
        orderBundles(
          first: $limit
          after: $after
          order: { createdAt: "DESC" }
          orders_status_list: [0, 1, 3, 4, 5, 6, 7]
          older_than_createdAt: "2 days ago"
        ) {
          pageInfo {
            startCursor
            endCursor
            hasNextPage
          }
          edges {
            node {
              _id
              amount
              createdAt
              payment {
                id
                _id
                status
                debitDate
                currencyIsoCode
                amount
                requested
                refundDate
              }
              orders {
                edges {
                  node {
                    _id
                    id
                    amount
                    type
                    status
                    createdAt
                    delayedDeliveryDate
                    campaign {
                      _id
                    }
                    returnOrders {
                      edges {
                        node {
                          id
                          description
                          status
                          reason
                          createdAt
                        }
                      }
                    }
                    product {
                      id
                      name
                      productImages
                      productGroupSize {
                        _id
                        id
                        size {
                          _id
                          id
                          label
                          extraLabel
                        }
                        productGroup {
                          _id
                          id
                          name
                          displayName
                        }
                      }
                      design {
                        name
                        primaryColor
                        secondaryColor
                        release {
                          expectedDeliveryDate
                          releaseDate
                        }
                      }
                    }
                    returnOrders {
                      edges {
                        node {
                          id
                          description
                          status
                          reason
                          createdAt
                        }
                      }
                    }
                    shipment {
                      id
                      deliveryDate
                      status
                    }
                    member {
                      _id
                      id
                      firstName
                      lastName
                      fullName
                      country {
                        name
                        vat {
                          value
                        }
                      }
                      postalAddress {
                        street
                        houseNumber
                        postalCode
                        city
                        annex
                        building
                        floor
                        streetType
                        region
                        country {
                          name
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `
    const data = await graphQLClient.request<OrderBundlesDataType>(query, variables)

    return {
      pageInfo: data.orderBundles.pageInfo,
      orderBundles: extractOrderBundles(data.orderBundles.edges)
    }
  } catch (error) {
    if (error instanceof Error) {
      Sentry.captureException({ error })
      throw new Error(error.message)
    }
  }
}

export const getLoyaltyPoints = async () => {
  try {
    const query = gql`
      query spaazaWallet {
        getSpaazaWallet {
          amount
          currency
        }
      }
    `

    const data = await graphQLClient.request<GetLoyaltyPointsDataType>(query)

    return data
  } catch (error) {
    if (error instanceof Error) {
      Sentry.captureException({ error })
      throw new Error(error.message)
    }
  }
}

export const getCountries = async () => {
  try {
    const response: AxiosResponse<GetCountriesResponse> = await axios.get(
      `${process.env.NEXT_PUBLIC_MAIN_WEBSITE_BASEURL}/api/v2/countries`
    )

    return response.data
  } catch (error) {
    if (error instanceof Error) {
      Sentry.captureException({ error })
      throw new Error(error.message)
    }
  }
}
